import { ReactElement, useCallback, useState } from 'react';
import {
  ArrowRightIcon,
  EnvelopeIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { AuthButtons, Button, Input, getGclid } from '@sc/shared-ui';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@apollo/client/react';
import { useRouter, useSearch } from '@tanstack/react-router';
import { CombinedGraphQLErrors } from '@apollo/client';
import {
  GQL_MUTATION_REGISTER,
  GQL_MUTATION_RESEND_VERIFICATION_EMAIL,
} from '../utility/queries';
import {
  AuthAnnouncement,
  AuthLayout,
  AuthPrivacyNotice,
  AuthTitle,
  useResendCountdown,
} from '../components/AuthLayout';
import { useOAuthCallback } from '../hooks/useOAuthCallback';
import { trackSignUpConversion } from '../utility/trackSignUpConversion';
import { track } from '../utility/analytics';
import { useAlerts } from '../providers/alerts';
import env from '../environment';

type StepType = 'details' | 'options';

export const Register = (): ReactElement => {
  const router = useRouter();

  const params = useSearch({ from: '/_unauthenticated/register' });

  const { useApolloError } = useAlerts();

  const onError = useApolloError();

  // login already collected the email, so skip the picker
  const step: StepType = params.step ?? (params.email ? 'details' : 'options');

  const [sentTo, setSentTo] = useState<string | null>(null);

  const setStep = useCallback(
    (next: StepType) =>
      void router.navigate({
        to: '/register',
        search: {
          step: next,
          ...(params.email ? { email: params.email } : {}),
        },
      }),
    [router, params.email]
  );

  // SSO accounts arrive fully usable — new ones go straight to creating
  // their first knowledge base
  const { isAuthorising } = useOAuthCallback({
    code: params.code,
    state: params.state,
    onCompleted: (registered) =>
      void router.navigate(
        registered ? { to: '/kb', hash: 'create' } : { to: '/' }
      ),
    onFailed: () => setStep('options'),
  });

  const { remaining, isCountingDown, restart } = useResendCountdown();

  const [resendVerificationEmail, { loading: isResending }] = useMutation(
    GQL_MUTATION_RESEND_VERIFICATION_EMAIL,
    {
      onCompleted: () => {
        track('Verification Email Resent', { context: 'sign-up' });

        restart();
      },
      onError,
    }
  );

  const [register, { loading: isRegistering }] = useMutation(
    GQL_MUTATION_REGISTER,
    {
      onError: (err) => {
        if (
          CombinedGraphQLErrors.is(err) &&
          err.errors.some((e) => e.extensions?.code === 'ACCOUNT_EXISTS')
        ) {
          void router.navigate({
            to: '/login',
            search: { email: form.state.values.email },
          });

          return;
        }

        onError(err);
      },
    }
  );

  const form = useForm({
    defaultValues: {
      email: params.email ?? '',
    },
    onSubmit: async ({ value }) => {
      await register({
        variables: { input: { email: value.email, gclid: getGclid() } },
        onCompleted: (data) => {
          trackSignUpConversion(data.register.companyUuid);

          track('Sign Up Email Submitted');

          restart();

          setSentTo(value.email);
        },
      });
    },
  });

  if (isAuthorising)
    return (
      <AuthLayout showPrivacy showLockUp loading>
        <AuthAnnouncement Icon={UserIcon} title="Creating your account">
          Standby, we are just setting your account up.
        </AuthAnnouncement>
      </AuthLayout>
    );

  if (sentTo)
    return (
      <AuthLayout showPrivacy showLockUp>
        <AuthAnnouncement Icon={EnvelopeIcon} title="We've sent you an email!">
          To continue, click the link sent to{' '}
          <span className="text-white font-semibold">{sentTo}</span>
        </AuthAnnouncement>

        <Button
          button={
            isCountingDown
              ? {
                text: `Resend in ${remaining}s`,
                size: 'large',
                disabled: true,
              }
              : {
                text: 'Resend email',
                size: 'large',
                type: 'basic',
                loading: isResending,
                disabled: isResending,
                onClick: () => void resendVerificationEmail(),
              }
          }
        />
      </AuthLayout>
    );

  if (step === 'options')
    return (
      <AuthLayout
        showLockUp
        footer={
          <>
            <Button
              button={{
                text: 'Already have an account? Log in',
                type: 'basic',
                size: 'large',
              }}
              link={{ to: '/login' }}
            />
            <AuthPrivacyNotice />
          </>
        }
      >
        <AuthTitle subtitle="Select one of the options below">
          Create a free account
        </AuthTitle>

        <div className="w-full flex flex-col gap-3">
          <AuthButtons
            authUrl={env.authUrl}
            appUrl={env.appUrl}
            cognitoClientId={env.cognitoClientId}
            action="register"
          />
          <Button
            button={{
              text: 'Register with email',
              IconEnd: EnvelopeIcon,
              type: 'primary',
              size: 'large',
              onClick: () => setStep('details'),
            }}
          />
        </div>
      </AuthLayout>
    );

  return (
    <AuthLayout
      showPrivacy
      showLockUp
      loading={isRegistering}
      onBack={() => setStep('options')}
    >
      <AuthTitle>Create account</AuthTitle>

      <form
        onSubmit={(e) => {
          e.stopPropagation();

          e.preventDefault();

          void form.handleSubmit();
        }}
        className="w-full space-y-5"
      >
        <form.Field
          name="email"
          children={(field) => (
            <Input
              value={field.state.value}
              label="Email address"
              type="email"
              required
              Icon={EnvelopeIcon}
              onChange={(e) => field.handleChange(e.target.value)}
            />
          )}
        />

        <Button
          button={{
            isSubmit: true,
            text: 'Continue',
            IconEnd: ArrowRightIcon,
            type: 'primary',
            size: 'large',
            loading: isRegistering,
            disabled: isRegistering,
          }}
        />
      </form>
    </AuthLayout>
  );
};
